import { drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { CasinoGame, TableOption } from './types';

/**
 * Single-zero (European) roulette: every bet carries the same 2.7% edge.
 * One bet per spin. "Bet on" opens a list of bet types; "Single number"
 * opens a second list to scroll to the number. Double-tap backs out of
 * either list without changing anything.
 */
export type RouletteBet = 'red' | 'black' | 'odd' | 'even' | 'low' | 'high' | 'dozen1' | 'dozen2' | 'dozen3' | 'straight';

const BET_TYPES: RouletteBet[] = ['red', 'black', 'odd', 'even', 'low', 'high', 'dozen1', 'dozen2', 'dozen3', 'straight'];

const BET_NAMES: Record<RouletteBet, string> = {
  red: 'Red',
  black: 'Black',
  odd: 'Odd',
  even: 'Even',
  low: '1-18',
  high: '19-36',
  dozen1: '1st dozen',
  dozen2: '2nd dozen',
  dozen3: '3rd dozen',
  straight: 'Single number',
};

export const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);

export function colorOf(n: number): 'red' | 'black' | 'green' {
  return n === 0 ? 'green' : RED.has(n) ? 'red' : 'black';
}

export function isEvenMoney(bet: RouletteBet): boolean {
  return ['red', 'black', 'odd', 'even', 'low', 'high'].includes(bet);
}

/** Total returned (stake included) when `ball` lands. */
export function rouletteReturn(bet: RouletteBet, number: number, ball: number, stake: number): number {
  if (ball === 0) return bet === 'straight' && number === 0 ? stake * 36 : 0;
  const wins: Record<RouletteBet, boolean> = {
    red: RED.has(ball),
    black: !RED.has(ball),
    odd: ball % 2 === 1,
    even: ball % 2 === 0,
    low: ball <= 18,
    high: ball >= 19,
    dozen1: ball <= 12,
    dozen2: ball >= 13 && ball <= 24,
    dozen3: ball >= 25,
    straight: ball === number,
  };
  if (!wins[bet]) return 0;
  if (bet === 'straight') return stake * 36;
  if (bet.startsWith('dozen')) return stake * 3;
  return stake * 2;
}

/** A bet's results over the 37 pockets (for the ★'s second-chance rate). */
export function rouletteOutcomes(bet: RouletteBet, number: number): Outcome[] {
  return Array.from({ length: 37 }, (_, ball) => [1 / 37, rouletteReturn(bet, number, ball, 1)] as const);
}

export interface RouletteState {
  phase: 'ready' | 'pickType' | 'pickNumber';
  bet: RouletteBet;
  number: number;
  /** Recent results, newest first. */
  history: number[];
  lastStake: number;
  returned: number;
  /** Consecutive straight-up wins (the jackpot needs two). */
  straightStreak: number;
  spun: boolean;
  /** The ★'s luck re-spun a losing spin. */
  lucky?: boolean;
}

function betLabel(state: RouletteState): string {
  return state.bet === 'straight' ? `Number ${state.number}` : BET_NAMES[state.bet];
}

/** The pockets in wheel order (single zero), as the ball meets them. */
export const WHEEL_ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
/** Frames of a spin: the pockets roll past the pointer and slow to a stop. */
const SPIN_FRAMES = 8;

/** Five pockets around the pointer at wheel position `at`, as rows. */
function wheelWindow(at: number): string[] {
  const n = WHEEL_ORDER.length;
  return [2, 1, 0, -1, -2].map((o) => {
    const label = ballLabel(WHEEL_ORDER[(((at + o) % n) + n) % n]!);
    return o === 0 ? `  > ${label} <` : `    ${label}`;
  });
}

/** The ball running round from wheel position `from` into `ball`'s pocket,
 *  fast then slowing (the last frame is one short; the result shows next). */
export function spinFrames(from: number, ball: number): string[] {
  const n = WHEEL_ORDER.length;
  const to = WHEEL_ORDER.indexOf(ball);
  const travel = n + ((to - from + n) % n);
  return Array.from({ length: SPIN_FRAMES }, (_, f) => {
    const t = (f + 1) / (SPIN_FRAMES + 1);
    return ['Spinning...', ...wheelWindow(from + Math.round(travel * (1 - (1 - t) ** 3)))].join('\n');
  });
}

function ballLabel(n: number): string {
  const color = colorOf(n);
  return `${n} ${color === 'green' ? 'GREEN' : color === 'red' ? 'RED' : 'BLACK'}`;
}

export const roulette: CasinoGame<RouletteState> = {
  id: 'roulette',
  usesLuck: true,
  name: 'Roulette',
  blurb: 'Single-zero wheel. Colours, halves, dozens, or one number.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a losing spin is sometimes spun again.',
  jackpotRule: 'Hit a single number twice in a row.',
  lockedHint: '• choose  •• cancel',
  create: () => ({ phase: 'ready', bet: 'red', number: 17, history: [], lastStake: 0, returned: 0, straightStreak: 0, spun: false }),
  // The pickers count as mid-play: no shared Bet option, and double-tap
  // cancels the picker (see gesture) instead of leaving the table.
  locked: (state) => state.phase !== 'ready',
  options(state): TableOption[] {
    if (state.phase === 'pickType') return BET_TYPES.map((t) => ({ id: `type:${t}`, label: BET_NAMES[t] }));
    if (state.phase === 'pickNumber') {
      return Array.from({ length: 37 }, (_, n) => ({ id: `num:${n}`, label: `${n} ${colorOf(n) === 'green' ? '' : colorOf(n)}`.trim() }));
    }
    return [
      { id: 'spin', label: 'Spin' },
      { id: 'pick', label: `Bet on: ${betLabel(state)}` },
    ];
  },
  describe(state, _env, highlighted) {
    if (state.phase === 'pickType') {
      const t = highlighted?.replace('type:', '') as RouletteBet | undefined;
      const pays = t === 'straight' ? '35:1' : t?.startsWith('dozen') ? '2:1' : '1:1';
      return `Pays ${pays}.`;
    }
    if (state.phase === 'pickNumber') return 'Pays 35:1.';
    const history = state.history.length ? `Last: ${state.history.slice(0, 6).join(' ')}` : '';
    if (!state.spun) return [`Betting on ${betLabel(state)}.`, history].filter(Boolean).join('\n\n');
    const net = state.returned - state.lastStake;
    const outcome = net > 0 ? `${state.lucky ? '★ ' : ''}+$${net}` : net === 0 ? 'Push' : `-$${-net}`;
    return [`Ball: ${ballLabel(state.history[0]!)}`, `${betLabel(state)}: ${outcome}`, '', history].join('\n');
  },
  act(state, optionId, env) {
    if (optionId === 'pick') return { state: { ...state, phase: 'pickType' }, bankroll: env.bankroll };
    if (optionId.startsWith('type:')) {
      const bet = optionId.slice(5) as RouletteBet;
      return { state: { ...state, bet, phase: bet === 'straight' ? 'pickNumber' : 'ready' }, bankroll: env.bankroll };
    }
    if (optionId.startsWith('num:')) {
      return { state: { ...state, bet: 'straight', number: Number(optionId.slice(4)), phase: 'ready' }, bankroll: env.bankroll };
    }
    if (optionId !== 'spin' || state.phase !== 'ready') return { state, bankroll: env.bankroll };
    // The ★'s luck may re-spin a losing spin (only the re-spin is shown).
    const q = secondChanceRate(rouletteOutcomes(state.bet, state.number), luckOf(env));
    const { outcome: ball, lucky, first } = drawWithLuck(env.rng, () => env.rng.int(37), (b) => rouletteReturn(state.bet, state.number, b, 1) < 1, q);
    const returned = rouletteReturn(state.bet, state.number, ball, env.bet);
    const hitStraight = state.bet === 'straight' && ball === state.number;
    const straightStreak = hitStraight ? state.straightStreak + 1 : 0;
    return {
      state: { ...state, history: [ball, ...state.history].slice(0, 8), lastStake: env.bet, returned, straightStreak, spun: true, lucky },
      bankroll: env.bankroll - env.bet + returned,
      settled: { wagered: env.bet, returned },
      jackpot: straightStreak >= 2,
      // The ball runs round the wheel into its pocket; a ★ second chance
      // shows it landing in the losing pocket first, then spins again.
      frames: first === undefined
        ? spinFrames(WHEEL_ORDER.indexOf(state.history[0] ?? 0), ball)
        : [
            ...spinFrames(WHEEL_ORDER.indexOf(state.history[0] ?? 0), first),
            secondChanceFrame(['Ball: ' + ballLabel(first), ...wheelWindow(WHEEL_ORDER.indexOf(first))].join('\n')),
            ...spinFrames(WHEEL_ORDER.indexOf(first), ball),
          ],
    };
  },
  gesture(state, gesture, env) {
    // Double-tap backs out of the pickers instead of leaving the table.
    if (gesture === 'double' && state.phase !== 'ready') return { state: { ...state, phase: 'ready' }, bankroll: env.bankroll };
    return null;
  },
};
